import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Baby, Ruler, TrendingUp, Camera, Users, Shield } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Camera,
      title: 'Camera Measurement',
      description: 'Use your phone camera with a reference object for accurate measurements'
    },
    {
      icon: Ruler,
      title: 'Manual Input',
      description: 'Manually enter foot measurements for quick size conversion'
    },
    {
      icon: TrendingUp,
      title: 'Growth Tracking',
      description: 'Track your baby\'s foot growth over time with beautiful charts'
    },
    {
      icon: Users,
      title: 'Multiple Children',
      description: 'Manage measurements for all your children in one place'
    },
    {
      icon: Shield,
      title: 'Size Recommendations',
      description: 'Get age-appropriate shoe size recommendations in US, EU, and UK sizes'
    },
    {
      icon: Baby,
      title: 'Ages 0-2 Years',
      description: 'Specialized for babies and toddlers with accurate size charts'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5]">
      {/* Header */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#FFB4A2] to-[#F7DBA7] rounded-full flex items-center justify-center p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_footsmart-3/artifacts/mzt1zwjs_WhatsApp%20Image%202026-03-31%20at%2010.36.19.jpeg" 
                alt="FeetSmart-Ai Logo" 
                className="w-full h-full object-cover rounded-full"
              />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] bg-clip-text text-transparent">
              FeetSmart-Ai
            </span>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')}>
              Login
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-[#FF9A85] hover:via-[#F5D085] hover:to-[#90C5FF] text-white"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="inline-block mb-4 px-4 py-2 bg-[#D9F2FF] rounded-full">
          <span className="text-[#6BA3D5] font-medium text-sm">Perfect for ages 0-2 years</span>
        </div>
        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
          Measure Your Baby's Feet
          <br />with Confidence
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Accurate foot measurements and shoe size recommendations for your little ones. Track growth over time and never buy the wrong shoe size again.
        </p>
        <div className="flex gap-4 justify-center">
          <Button 
            size="lg"
            className="bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white text-lg px-8"
            onClick={() => navigate('/register')}
          >
            Start Measuring
          </Button>
          <Button 
            size="lg"
            variant="outline"
            className="text-lg px-8"
            onClick={() => navigate('/size-guide')}
          >
            View Size Guide
          </Button>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
          Everything You Need
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white/80 backdrop-blur-sm border-2 border-transparent hover:border-[#D9F2FF]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FFB4A2] to-[#F7DBA7] rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <Card className="p-12 bg-gradient-to-br from-pink-400 via-purple-400 to-blue-400 border-0 text-white text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join parents who trust FeetSmart-Ai for accurate children shoe sizing
          </p>
          <Button 
            size="lg"
            className="bg-white text-[#A8D5FF] hover:bg-gray-100 text-lg px-8"
            onClick={() => navigate('/register')}
          >
            Create Free Account
          </Button>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white/80 backdrop-blur-sm py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-600">
          <p>© 2026 FeetSmart-Ai. Helping parents find the perfect fit.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;